import Vue from 'vue';
import router from './router';
import { VueExtendLayout, layout } from 'vue-extend-layout';
import Vuetify from 'vuetify';
import 'vuetify/dist/vuetify.min.css';
import 'material-icons/iconfont/material-icons.css';
import 'typeface-roboto';
import store from './store';
import Vue2Filters from 'vue2-filters';
import vueg from 'vueg';
import VueProgressBar from 'vue-progressbar';
import axios from 'axios';
import Snotify, { SnotifyPosition } from 'vue-snotify';
import 'vue-snotify/styles/material.css';
import config from './config';
import { VENUE_STORAGE } from '@/constants';
import './styles/global.css';
import { client, registerSessionInvalidationHandler } from './services';

let isRedirecting = false;
let _sessionPollInterval = null;

// ─── Axios interceptors ───────────────────────────────────────────────────────

axios.interceptors.request.use(
  config => {
    const token = localStorage.getItem('feathers-jwt');
    if (
      token &&
      !config.url.endsWith('/authentication') &&
      !config.url.includes('/verify-jwt') &&
      !config.url.includes('/socket.io/')
    ) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }

    const method = (config.method || 'get').toUpperCase();
    const unsafe = method !== 'GET' && method !== 'HEAD' && method !== 'OPTIONS';
    if (unsafe && !config.headers['X-CSRF-Token']) {
      const match = document.cookie.split('; ').find(row => row.startsWith('XSRF-TOKEN='));
      if (match) {
        config.headers['X-CSRF-Token'] = decodeURIComponent(match.split('=')[1]);
      }
    }

    return config;
  },
  error => Promise.reject(error)
);

// Redirect to login if session was invalidated via HTTP 401
axios.interceptors.response.use(
  response => response,
  error => {
    if (error.response && error.response.status === 401) {
      const errorMessage = (error.response.data && error.response.data.message) || '';
      const errorCode = (error.response.data && error.response.data.code) || '';

      const isSessionInvalidated =
        errorMessage.includes('Session invalidated') ||
        errorMessage.includes('password change') ||
        errorCode === 'TOKEN_BLACKLISTED';

      if (isSessionInvalidated && !isRedirecting) {
        isRedirecting = true;
        clearSession();
        setTimeout(() => {
          window.location.replace('/');
        }, 100);
      }
    }
    return Promise.reject(error);
  }
);

// ─── Feathers error hook — catch 401 from socket calls ───────────────────────

client.hooks({
  error: {
    all: [
      context => {
        const error = context.error;
        const is401 = error && (error.code === 401 || error.name === 'NotAuthenticated');

        if (is401 && !isRedirecting) {
          isRedirecting = true;
          stopSessionPolling();
          clearSession();

          store.commit('users/SET_USER', null);
          store.commit('users/SET_AUTH_STATE', false);

          if (window.app && window.app.$snotify) {
            window.app.$snotify.warning(
              'Your session has expired. Please login again.',
              'Session Expired',
              {
                timeout: 2000,
                showProgressBar: true
              }
            );
          }

          setTimeout(() => {
            isRedirecting = false;
            window.location.replace('/');
          }, 1000);
        }

        return context;
      }
    ]
  }
});

// ─── Vue setup ────────────────────────────────────────────────────────────────

Vue.config.productionTip = false;
Vue.use(VueExtendLayout);

const selected = JSON.parse(localStorage.getItem(VENUE_STORAGE));
if (selected && selected.primaryColor && selected.secondaryColor) {
  setBannerColor(selected.primaryColor, selected.secondaryColor);
} else {
  setBannerColor();
}

Vue.use(Vue2Filters);
Vue.use(vueg, router, { shadow: false, forwardAnim: 'fadeIn', backAnim: 'fadeIn' });
Vue.use(VueProgressBar);
Vue.use(Snotify, { toast: { position: SnotifyPosition.rightTop, timeout: 2000 } });

Vue.prototype.$axios = axios;
Vue.prototype.$ROOT_URL = config.server_url;

// ─── Helpers ──────────────────────────────────────────────────────────────────

function setBannerColor(
  primaryColor = config.theme.peterBlue,
  secondaryColor = config.theme.sidebarColor
) {
  Vue.use(Vuetify, {
    theme: { ...config.theme, peterBlue: primaryColor, sidebarColor: secondaryColor, fontsize: 14 }
  });
}

// Remove JWT from storage and cookies
function clearSession() {
  localStorage.removeItem('feathers-jwt');
  localStorage.removeItem('SELECTED_CMS_VENUE');
  document.cookie = 'feathers-jwt=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/';
}

// Register socket listener — auto-logout when password is changed on another browser
function setupSessionInvalidationListener(user) {
  if (!user || !user._id) return;

  registerSessionInvalidationHandler(user._id, function(data) {
    stopSessionPolling();
    clearSession();

    store.commit('users/SET_USER', null);
    store.commit('users/SET_AUTH_STATE', false);

    const message =
      data.reason === 'password_change'
        ? 'Your password was changed. Please login again.'
        : 'Your session has expired. Please login again.';

    if (window.app && window.app.$snotify) {
      window.app.$snotify.warning(message, 'Session Expired', {
        timeout: 3000,
        showProgressBar: true,
        closeOnClick: true,
        pauseOnHover: true
      });
    }

    setTimeout(() => {
      window.location.replace('/');
    }, 1000);
  });
}

// Poll server every 3s to detect invalidated sessions (fallback for missed socket events)
function startSessionPolling() {
  if (_sessionPollInterval) {
    clearInterval(_sessionPollInterval);
  }

  _sessionPollInterval = setInterval(async () => {
    const token = localStorage.getItem('feathers-jwt');
    if (!token || isRedirecting) return;

    try {
      const currentUser = store.state.users.user;
      if (!currentUser || !currentUser._id) return;
      await client.service('users').get(currentUser._id);
    } catch (e) {
      const isInvalid =
        e.code === 401 ||
        e.name === 'NotAuthenticated' ||
        (e.message && e.message.includes('Session invalidated'));

      if (isInvalid && !isRedirecting) {
        isRedirecting = true;
        stopSessionPolling();
        clearSession();

        store.commit('users/SET_USER', null);
        store.commit('users/SET_AUTH_STATE', false);

        if (window.app && window.app.$snotify) {
          window.app.$snotify.warning(
            'Your session has expired. Please login again.',
            'Session Expired',
            {
              timeout: 3000,
              showProgressBar: true
            }
          );
        }

        setTimeout(() => {
          isRedirecting = false;
          window.location.replace('/');
        }, 1000);
      }
    }
  }, 3000);
}

function stopSessionPolling() {
  if (_sessionPollInterval) {
    clearInterval(_sessionPollInterval);
    _sessionPollInterval = null;
  }
}

// Keep JWT cookie in sync with localStorage (cookie can expire before localStorage)
function setupCookieSync() {
  function syncCookie() {
    const jwtFromStorage = localStorage.getItem('feathers-jwt');
    const jwtFromCookie = getCookie('feathers-jwt');

    if (jwtFromStorage && !jwtFromCookie) {
      const isSecure = window.location.protocol === 'https:';
      const maxAge = 2 * 60 * 60;
      const secureSuffix = isSecure ? '; Secure' : '';
      document.cookie = `feathers-jwt=${jwtFromStorage}; path=/; max-age=${maxAge}; SameSite=Lax${secureSuffix}`;
    }
  }

  function getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2)
      return parts
        .pop()
        .split(';')
        .shift();
  }

  syncCookie();
  setInterval(syncCookie, 60000);
}

// ─── Fetch initial configs ────────────────────────────────────────────────────

async function fetchCsrfToken() {
  try {
    const { data } = await axios.get(`${config.server_url}/csrf-token`, { withCredentials: true });
    if (data && data.csrfToken) {
      axios.defaults.headers.common['X-CSRF-Token'] = data.csrfToken;
    }
  } catch (e) {
    console.warn('Unable to fetch CSRF token:', e && e.message);
  }
}
fetchCsrfToken();

axios
  .get(`${config.server_url}/get_api_url_config`)
  .then(response => {
    Vue.prototype.$API_URL = response.data.api_url;
  })
  .catch(error => {
    console.error('Error fetching API URL config:', error);
  });

const VBtn = Vue.component('VBtn');
VBtn.options.props.ripple.default = false;

// ─── App init ─────────────────────────────────────────────────────────────────

let app;

async function init() {
  if (app) return;

  // Wait for DOM to be ready before mounting Vue
  await new Promise(resolve => {
    if (document.readyState === 'complete' || document.readyState === 'interactive') {
      resolve();
    } else {
      document.addEventListener('DOMContentLoaded', resolve);
    }
  });

  try {
    const response = await axios.get(config.server_url + '/api/settings/cms-version');
    if (response.data.success) {
      store.commit('SET_VERSION', {
        version: response.data.version,
        isFullVersion: response.data.isFullVersion
      });
    }
  } catch (error) {
    console.warn('Error getting CMS version:', error.message);
  }

  let authenticatedUser = null;

  try {
    if (localStorage.getItem('feathers-jwt')) {
      const jwt = localStorage.getItem('feathers-jwt');
      const isSecure = window.location.protocol === 'https:';
      const maxAge = 2 * 60 * 60;
      const secureSuffix = isSecure ? '; Secure' : '';
      document.cookie = `feathers-jwt=${jwt}; path=/; max-age=${maxAge}; SameSite=Lax${secureSuffix}`;

      const response = await client.authenticate();
      const payload = await client.passport.verifyJWT(response.accessToken);
      authenticatedUser = await client.service('users').get(payload.userId);
    }
  } catch (e) {
    console.warn('Authentication failed:', e.message);
  }

  if (authenticatedUser) {
    store.commit('users/SET_USER', authenticatedUser);
    if (authenticatedUser.venues && authenticatedUser.venues.length > 0) {
      await store.dispatch('users/GET_USER_VENUES', authenticatedUser.venues);
    }
    store.commit('users/SET_AUTH_STATE', true);
  } else {
    store.commit('users/SET_AUTH_STATE', false);
  }

  app = new Vue({ el: '#app', store, router, ...layout });
  window.app = app;

  if (authenticatedUser) {
    setupSessionInvalidationListener(authenticatedUser);
    setupCookieSync();
    startSessionPolling();
  }

  // Watch for login/logout after app is mounted (e.g. user logs in via Login page without page reload)
  store.watch(
    state => state.users.isAuthenticated,
    (isAuthenticated, wasAuthenticated) => {
      if (isAuthenticated && !wasAuthenticated) {
        const user = store.state.users.user;
        if (user && user._id) {
          setupSessionInvalidationListener(user);
          startSessionPolling();
        }
      } else if (!isAuthenticated && wasAuthenticated) {
        stopSessionPolling();
      }
    }
  );
}

init();



// WEBPACK FOOTER //
// ./src/main.js